import { useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Calendar, Clock } from "lucide-react";
import { JOB_STEPS } from "../CreateJobPage";
import CustomInputV2 from "@components/CustomInputV2/CustomInputV2";
import FieldError from "./FieldError";
import { JobFormValues } from "../schema/jobForm.schema";

const publicationTypes = [
  {
    label: "Publish after payment",
    desc: "The post becomes visible as soon as the stored-card payment is authorised.",
    value: 'Publish_After_Payment',
    isRecommended: true,
  },
  {
    label: "Schedule publication",
    desc: "Authorise payment now and choose the date and time the post becomes visible.",
    value: "Schedule_Publication",
    isRecommended: false,
  },
] as const;

const Step7 = ({ activeStep }) => {
  const {
    control,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<JobFormValues>();
  const [
    selectedPublicationType,
    publicationDate,
    publicationTime,
    publicationTimezone,
  ] = useWatch({
    control,
    name: [
      "publicationType",
      "publicationDate",
      "publicationTime",
      "publicationTimezone",
    ],
  });
  const dateInputRef = useRef<HTMLInputElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);
  const { ref: registerDateRef, ...dateField } = register("publicationDate");
  const { ref: registerTimeRef, ...timeField } = register("publicationTime");

  const scheduledAt =
    publicationDate && publicationTime
      ? new Date(`${publicationDate}T${publicationTime}`)
      : null;
  const publishingPlan =
    selectedPublicationType === 'Publish_After_Payment'
      ? "Publish immediately after payment"
      : scheduledAt
        ? `${scheduledAt.toLocaleDateString(undefined, {
            weekday: "short",
            day: "2-digit",
            month: "short",
            year: "numeric",
          })} at ${publicationTime} · ${publicationTimezone}`
        : "Choose a publication date and time";

  return (
    <>
      {/* Header */}
      <div className="flex border-b pb-3">
        <div className="flex flex-col gap-0.5 flex-1">
          <span className="text-xss font-bold text-light-black uppercase">
            Step {String(activeStep).padStart(2, "0")} /{" "}
            {JOB_STEPS[activeStep - 1]?.label}
          </span>
          <span className="font-semibold text-xl">
            {JOB_STEPS[activeStep - 1]?.title}
          </span>
          <span className="text-xss font-medium text-light-black">
            {JOB_STEPS[activeStep - 1]?.subtitle}
          </span>
        </div>
        <span className="self-end text-xss font-bold text-light-black uppercase leading-3">
          {JOB_STEPS[activeStep - 1]?.tagline}
        </span>
      </div>
      {/* Publication timing */}
      <div className="rounded-md border overflow-hidden mt-5">
        <div className="flex items-center justify-between border-b px-4 py-2.5 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center rounded-full h-7 w-7 bg-slate-50 border text-xss font-bold text-light-black">
              01
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-xs">
                When should this job post go live?
              </span>
              <span className="text-xss font-medium text-light-black">
                Publish after payment or authorise payment now and schedule a
                future release.
              </span>
            </div>
          </div>

          <div className="rounded-full px-2 py-1 font-bold text-xsss text-green-800 uppercase bg-green-100">
            Publication control
          </div>
        </div>

        <div className="flex flex-col p-4 gap-4">
          {/* Timing Type */}
          <div className="flex gap-3">
            {publicationTypes?.map((publicationType) => {
              const isSelected =
                publicationType?.value === selectedPublicationType;
              return (
                <button
                  key={publicationType.value}
                  type="button"
                  onClick={() =>
                    setValue("publicationType", publicationType.value, {
                      shouldDirty: true,
                    })
                  }
                  className={`flex rounded-md p-4 gap-3 flex-1 ${isSelected ? "border-l-[3.5px] border-slate-600 border bg-slate-50" : "border"}`}
                >
                  <div
                    className={`flex items-center justify-center rounded-full border w-4 h-4 shrink-0 ${isSelected ? "bg-slate-600" : "border-light-grey"}`}
                  >
                    <div className="h-1.5 w-1.5 rounded-full bg-white" />
                  </div>
                  <div className="flex flex-col gap-1 flex-1 text-left">
                    <span className="text-black text-sm font-semibold">
                      {publicationType?.label}
                    </span>
                    <span className="text-xss text-light-black font-medium">
                      {publicationType?.desc}
                    </span>
                  </div>
                  {publicationType?.isRecommended ? (
                    <div className="bg-green-100 text-green-800 rounded-full py-0.5 px-3 uppercase font-bold text-xsss w-max h-max">
                      recommended
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>

          {/* Custom date time */}
          {selectedPublicationType === 'Schedule_Publication' ? (
            <div className="grid grid-cols-[1.2fr_0.8fr] gap-3 border rounded-md bg-slate-50 p-4">
              <div className="flex items-start flex-1 gap-3">
                <div className="flex flex-col gap-1.5 flex-1">
                  <label className="text-xss font-bold text-slate-600">
                    Publication date
                  </label>
                  <CustomInputV2
                    {...dateField}
                    ref={(element) => {
                      registerDateRef(element);
                      dateInputRef.current = element;
                    }}
                    type="date"
                    inputContainerStyle={
                      errors.publicationDate ? "border-orange-700" : ""
                    }
                    inputTextStyle="[&::-webkit-calendar-picker-indicator]:hidden"
                    renderRight={
                      <button
                        type="button"
                        onClick={() => dateInputRef.current?.showPicker?.()}
                        className="text-black"
                      >
                        <Calendar size={12} />
                      </button>
                    }
                  />
                  <FieldError message={errors.publicationDate?.message} />
                </div>
                <div className="flex flex-col gap-1.5 flex-1">
                  <label className="text-xss font-bold text-slate-600">
                    Publication time
                  </label>
                  <CustomInputV2
                    {...timeField}
                    ref={(element) => {
                      registerTimeRef(element);
                      timeInputRef.current = element;
                    }}
                    type="time"
                    inputContainerStyle={
                      errors.publicationTime ? "border-orange-700" : ""
                    }
                    inputTextStyle="[&::-webkit-calendar-picker-indicator]:hidden"
                    renderRight={
                      <button
                        type="button"
                        onClick={() => timeInputRef.current?.showPicker?.()}
                        className="text-black"
                      >
                        <Clock size={12} />
                      </button>
                    }
                  />
                  <FieldError message={errors.publicationTime?.message} />
                </div>
              </div>

              <div className="flex flex-col border-l-[3px] border-slate-600 bg-white px-5 justify-center rounded-r-md h-20 gap-0.5">
                <span className="uppercase font-bold text-xss text-slate-600 tracking-wider">
                  Schedule timezone
                </span>
                <span className="font-semibold text-xss">
                  {publicationTimezone}
                </span>
                <span className="text-xsss font-medium text-light-black">
                  The post remains paid and scheduled until this time.
                  Candidates cannot view or match against it beforehand.
                </span>
              </div>
            </div>
          ) : null}

          <div className="flex items-center justify-between border rounded-md px-3.5 py-3 gap-1.5 ">
            <label className="uppercase text-xsss font-bold text-light-black tracking-wider">
              publishing plan
            </label>
            <span className="text-black text-xss font-bold">
              {publishingPlan}
            </span>
          </div>

          <div className="flex items-center gap-5 border-l-[3px] border-slate-600 bg-slate-100 px-4 py-3.5">
            <span className="uppercase font-bold text-xss text-slate-600 shrink-0">
              Publication checkpoint
            </span>
            <span className="text-xss font-medium text-light-black">
              Payment authorisation happens on the next step. This setting
              controls when the post becomes visible; it does not charge the
              stored card.
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Step7;
